import React, { useMemo, useState } from 'react';
import { WEB3FORMS_ACCESS_KEY } from '../../config/web3forms';

// ------------------------------------------------------------------
// Tool Studio — interactive "request a custom tool" builder.
// Runs 100% client-side; the live estimate is a ballpark, confirmed
// on a call. Submissions POST to Web3Forms (static-host friendly).
// ------------------------------------------------------------------

const FALLBACK_EMAIL = 'info@buildlabconsulting.com';

const NAVY = '#020842', BLUE = '#0044EB', PINK = '#F44380', INK = '#16203C',
    SLATE = '#54648A', LINE = '#D7E2F1', GREEN = '#1F9D57';

const TYPES = [
    { id: 'calculator', label: 'Calculator', desc: 'Turns inputs into a number or result', base: [3000, 6000], weeks: [2, 3] },
    { id: 'estimator', label: 'Estimator / instant quote', desc: 'Returns a price, cost or range', base: [4000, 8000], weeks: [3, 4] },
    { id: 'checker', label: 'Checker / scorecard', desc: 'Grades or checks against rules', base: [3000, 6500], weeks: [2, 4] },
    { id: 'configurator', label: 'Configurator / wizard', desc: 'Guides choices to a spec or plan', base: [6000, 12000], weeks: [4, 6] },
    { id: 'dashboard', label: 'Dashboard', desc: 'Visualizes data and live metrics', base: [9000, 18000], weeks: [5, 8] },
    { id: 'unsure', label: 'Not sure yet', desc: "We'll help you scope it", base: [4000, 9000], weeks: [3, 5] },
];
const AUD = [
    { id: 'internal', label: 'My internal team', add: [0, 0], wk: 0 },
    { id: 'leadmagnet', label: 'A lead magnet for my customers', add: [600, 1200], wk: 0 },
    { id: 'portal', label: 'A client / partner portal', add: [1500, 3500], wk: 2 },
];
const INPUTS = [
    { id: 'few', label: 'A handful (1–5)', add: [0, 0] },
    { id: 'some', label: 'Moderate (6–15)', add: [1000, 2500] },
    { id: 'many', label: 'Lots (16+)', add: [3000, 6000] },
];
const ADDONS = [
    { id: 'whitelabel', label: 'White-label branding', add: [500, 1200], wk: 0 },
    { id: 'pdf', label: 'PDF / report export', add: [800, 1800], wk: 1 },
    { id: 'share', label: 'Save & share results', add: [1200, 2800], wk: 1 },
    { id: 'charts', label: 'Charts & visualizations', add: [800, 1600], wk: 0 },
    { id: 'integrations', label: 'Integrations / API', add: [2000, 5000], wk: 1 },
    { id: 'accounts', label: 'User logins & accounts', add: [3000, 7000], wk: 2 },
];
const TIMELINES = [
    { id: 'flexible', label: 'Flexible' },
    { id: 'month', label: 'Within a month' },
    { id: 'rush', label: 'ASAP / rush' },
];

const money = (n) => '$' + (Math.round(n / 500) * 500).toLocaleString('en-US');

function estimate(s) {
    const t = TYPES.find((x) => x.id === s.type);
    if (!t) return null;
    const au = AUD.find((x) => x.id === s.audience) || { add: [0, 0], wk: 0 };
    const inp = INPUTS.find((x) => x.id === s.inputs) || { add: [0, 0] };
    let lo = t.base[0] + au.add[0] + inp.add[0];
    let hi = t.base[1] + au.add[1] + inp.add[1];
    let wlo = t.weeks[0], whi = t.weeks[1] + au.wk;
    s.addons.forEach((id) => {
        const a = ADDONS.find((x) => x.id === id);
        if (a) { lo += a.add[0]; hi += a.add[1]; whi += a.wk; }
    });
    const mid = (lo + hi) / 2;
    const tier = mid < 7000 ? { name: 'Spark', color: GREEN }
        : mid < 16000 ? { name: 'Engine', color: BLUE }
            : { name: 'Platform', color: PINK };
    return { lo, hi, wlo, whi, tier };
}

const css = `
.ts *{box-sizing:border-box}
.ts{background:#fff;border:1px solid ${LINE};border-radius:20px;padding:clamp(20px,4vw,40px);box-shadow:0 30px 70px rgba(6,16,54,.08)}
.ts__grid{display:grid;grid-template-columns:1fr 344px;gap:clamp(24px,3vw,40px);align-items:start}
.ts__sec{padding:22px 0;border-top:1px solid #EEF2F8}
.ts__sec:first-child{border-top:none;padding-top:4px}
.ts__q{display:flex;gap:11px;align-items:baseline}
.ts__n{flex:none;width:26px;height:26px;border-radius:8px;background:${LIGHTBG()};color:${BLUE};font-family:'Axiforma Bold',sans-serif;font-size:13px;display:inline-flex;align-items:center;justify-content:center}
.ts__label{font-family:'Axiforma Bold',sans-serif;color:${NAVY};font-size:clamp(16px,2.3vw,18px)}
.ts__hint{font-family:'Axiforma Regular',sans-serif;color:${SLATE};font-size:14px;margin:2px 0 14px 37px}
.ts__cards{display:grid;grid-template-columns:1fr 1fr;gap:11px;margin-left:37px}
.ts__card{text-align:left;background:#fff;border:1.5px solid ${LINE};border-radius:12px;padding:13px 15px;cursor:pointer;transition:.15s;font-family:inherit}
.ts__card:hover{border-color:#B9CBEA}
.ts__card[aria-pressed=true]{border-color:${BLUE};background:#F3F7FF;box-shadow:0 0 0 3px rgba(0,68,235,.10)}
.ts__ct{font-family:'Axiforma SemiBold',sans-serif;color:${INK};font-size:15.5px}
.ts__cd{font-family:'Axiforma Regular',sans-serif;color:${SLATE};font-size:13px;margin-top:3px}
.ts__opts{display:flex;flex-wrap:wrap;gap:9px;margin-left:37px}
.ts__chip{background:#fff;border:1.5px solid ${LINE};border-radius:999px;padding:9px 16px;cursor:pointer;font-family:'Axiforma SemiBold',sans-serif;color:${INK};font-size:14px;transition:.15s}
.ts__chip:hover{border-color:#B9CBEA}
.ts__chip[aria-pressed=true]{border-color:${BLUE};background:${BLUE};color:#fff}
.ts__field{margin-left:37px}
.ts__field textarea,.ts__field input{width:100%;border:1.5px solid ${LINE};border-radius:11px;padding:12px 14px;font-family:'Axiforma Regular',sans-serif;font-size:15px;color:${INK};background:#fff;resize:vertical}
.ts__field textarea:focus,.ts__field input:focus{outline:none;border-color:${BLUE};box-shadow:0 0 0 3px rgba(0,68,235,.10)}
.ts__two{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.ts__panel{position:sticky;top:96px;background:${NAVY};border-radius:16px;padding:24px;color:#fff}
.ts__badge{display:inline-block;font-family:'Axiforma Bold',sans-serif;font-size:12px;letter-spacing:1px;padding:5px 12px;border-radius:999px;color:#fff}
.ts__price{font-family:'Axiforma Bold',sans-serif;font-size:34px;line-height:1.05;margin:14px 0 2px}
.ts__weeks{font-family:'Axiforma SemiBold',sans-serif;color:#9FB4FF;font-size:15px}
.ts__spec{list-style:none;margin:18px 0 0;padding:16px 0 0;border-top:1px solid rgba(255,255,255,.14)}
.ts__spec li{display:flex;justify-content:space-between;gap:10px;font-family:'Axiforma Regular',sans-serif;font-size:13.5px;color:#C9D6F5;padding:5px 0}
.ts__spec b{font-family:'Axiforma SemiBold',sans-serif;color:#fff;text-align:right}
.ts__disc{font-family:'Axiforma Regular',sans-serif;color:#7E8FC4;font-size:12px;line-height:1.45;margin-top:16px}
.ts__empty{font-family:'Axiforma Regular',sans-serif;color:#9FB4FF;font-size:14px;line-height:1.5}
.ts__btn{display:inline-flex;align-items:center;justify-content:center;width:100%;margin-top:8px;background:${BLUE};color:#fff;border:none;border-radius:12px;padding:15px;font-family:'Axiforma Bold',sans-serif;font-size:16px;cursor:pointer;transition:.15s}
.ts__btn:hover{background:#0037c4}
.ts__btn:disabled{opacity:.55;cursor:not-allowed}
.ts__msg{font-family:'Axiforma Regular',sans-serif;font-size:14px;margin-top:12px;line-height:1.5}
.ts__done{text-align:center;padding:30px 10px}
.ts__done h3{font-family:'Axiforma Bold',sans-serif;color:${NAVY};font-size:26px;margin:14px 0 8px}
.ts__done p{font-family:'Axiforma Regular',sans-serif;color:${SLATE};font-size:16px;line-height:1.55;max-width:460px;margin:0 auto}
@media(max-width:900px){.ts__grid{grid-template-columns:1fr}.ts__panel{position:static;order:-1}.ts__cards{grid-template-columns:1fr}}
`;
function LIGHTBG() { return '#E3F0FF'; }

export default function ToolStudio() {
    const [type, setType] = useState('');
    const [replaces, setReplaces] = useState('');
    const [audience, setAudience] = useState('');
    const [inputs, setInputs] = useState('');
    const [output, setOutput] = useState('');
    const [addons, setAddons] = useState([]);
    const [timeline, setTimeline] = useState('');
    const [name, setName] = useState('');
    const [company, setCompany] = useState('');
    const [email, setEmail] = useState('');
    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState('idle'); // idle | sending | done | error

    const est = useMemo(() => estimate({ type, audience, inputs, addons }), [type, audience, inputs, addons]);
    const toggleAddon = (id) => setAddons((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

    const typeLabel = TYPES.find((t) => t.id === type)?.label || '—';
    const audLabel = AUD.find((a) => a.id === audience)?.label || '—';
    const inpLabel = INPUTS.find((i) => i.id === inputs)?.label || '—';
    const addonLabels = addons.map((id) => ADDONS.find((a) => a.id === id)?.label).filter(Boolean);

    const brief = () =>
        `Tool type: ${typeLabel}\nReplaces: ${replaces || '—'}\nAudience: ${audLabel}\nInputs: ${inpLabel}\n` +
        `Output/decision: ${output || '—'}\nAdd-ons: ${addonLabels.join(', ') || 'none'}\nTimeline: ${TIMELINES.find((t) => t.id === timeline)?.label || '—'}\n` +
        (est ? `Ballpark: ${money(est.lo)}–${money(est.hi)} · ${est.wlo}–${est.whi} weeks · ${est.tier.name} tier\n` : '') +
        `\nFrom: ${name} — ${company}\nEmail: ${email}\nNotes: ${notes || '—'}`;

    const submit = async (e) => {
        e.preventDefault();
        if (!type || !name || !email) return;
        setStatus('sending');
        const payload = {
            access_key: WEB3FORMS_ACCESS_KEY,
            subject: `Tool Studio brief — ${company || name} (${est ? est.tier.name : 'scoping'})`,
            from_name: 'BuildLab Tool Studio',
            name, email, company,
            message: brief(),
        };
        try {
            const res = await fetch('https://api.web3forms.com/submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.success) setStatus('done');
            else setStatus('error');
        } catch (_) {
            setStatus('error');
        }
    };

    if (status === 'done') {
        return (
            <div className="ts">
                <style dangerouslySetInnerHTML={{ __html: css }} />
                <div className="ts__done">
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="11" fill="#E7F6EE" />
                        <path d="M7 12.5l3.2 3.2L17 9" stroke={GREEN} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <h3>Brief received.</h3>
                    <p>Thanks, {name.split(' ')[0] || 'there'}. We'll review your tool concept and reply within one business day with a scoped quote and a couple of clarifying questions.</p>
                </div>
            </div>
        );
    }

    return (
        <form className="ts" onSubmit={submit}>
            <style dangerouslySetInnerHTML={{ __html: css }} />
            <div className="ts__grid">
                <div>
                    {/* 1 — type */}
                    <div className="ts__sec">
                        <div className="ts__q"><span className="ts__n">1</span><span className="ts__label">What kind of tool?</span></div>
                        <div className="ts__hint">Pick the closest fit — we'll refine it together.</div>
                        <div className="ts__cards">
                            {TYPES.map((t) => (
                                <button type="button" key={t.id} className="ts__card" aria-pressed={type === t.id} onClick={() => setType(t.id)}>
                                    <div className="ts__ct">{t.label}</div>
                                    <div className="ts__cd">{t.desc}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* 2 — context */}
                    <div className="ts__sec">
                        <div className="ts__q"><span className="ts__n">2</span><span className="ts__label">What should it do?</span></div>
                        <div className="ts__hint">The manual process, spreadsheet, or decision it replaces.</div>
                        <div className="ts__field">
                            <textarea rows={3} value={replaces} onChange={(e) => setReplaces(e.target.value)}
                                placeholder="e.g. Right now our team rebuilds a 12-tab pricing spreadsheet for every quote…" />
                        </div>
                        <div className="ts__q" style={{ marginTop: 18 }}><span className="ts__n" style={{ visibility: 'hidden' }}>·</span><span className="ts__label" style={{ fontSize: 15 }}>Who's it for?</span></div>
                        <div className="ts__opts" style={{ marginTop: 10 }}>
                            {AUD.map((a) => (
                                <button type="button" key={a.id} className="ts__chip" aria-pressed={audience === a.id} onClick={() => setAudience(a.id)}>{a.label}</button>
                            ))}
                        </div>
                    </div>
                    {/* 3 — scope */}
                    <div className="ts__sec">
                        <div className="ts__q"><span className="ts__n">3</span><span className="ts__label">Roughly how many inputs?</span></div>
                        <div className="ts__hint">Fields a user fills in or selects.</div>
                        <div className="ts__opts">
                            {INPUTS.map((i) => (
                                <button type="button" key={i.id} className="ts__chip" aria-pressed={inputs === i.id} onClick={() => setInputs(i.id)}>{i.label}</button>
                            ))}
                        </div>
                        <div className="ts__field" style={{ marginTop: 16 }}>
                            <input value={output} onChange={(e) => setOutput(e.target.value)} placeholder="What should it output or decide? (optional)" />
                        </div>
                    </div>
                    {/* 4 — add-ons */}
                    <div className="ts__sec">
                        <div className="ts__q"><span className="ts__n">4</span><span className="ts__label">Any add-ons?</span></div>
                        <div className="ts__hint">Select any that apply — each nudges the estimate.</div>
                        <div className="ts__opts">
                            {ADDONS.map((a) => (
                                <button type="button" key={a.id} className="ts__chip" aria-pressed={addons.includes(a.id)} onClick={() => toggleAddon(a.id)}>{a.label}</button>
                            ))}
                        </div>
                    </div>
                    {/* 5 — timeline */}
                    <div className="ts__sec">
                        <div className="ts__q"><span className="ts__n">5</span><span className="ts__label">Timeline?</span></div>
                        <div className="ts__opts" style={{ marginTop: 12 }}>
                            {TIMELINES.map((t) => (
                                <button type="button" key={t.id} className="ts__chip" aria-pressed={timeline === t.id} onClick={() => setTimeline(t.id)}>{t.label}</button>
                            ))}
                        </div>
                    </div>
                    {/* 6 — contact */}
                    <div className="ts__sec">
                        <div className="ts__q"><span className="ts__n">6</span><span className="ts__label">Where do we send the quote?</span></div>
                        <div className="ts__field" style={{ marginTop: 14 }}>
                            <div className="ts__two">
                                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name *" required />
                                <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company" />
                            </div>
                            <input style={{ marginTop: 12 }} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email *" required />
                            <textarea style={{ marginTop: 12 }} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything else? (optional)" />
                        </div>
                    </div>
                </div>

                {/* live estimate panel */}
                <aside className="ts__panel" aria-live="polite">
                    {est ? (
                        <>
                            <span className="ts__badge" style={{ background: est.tier.color }}>{est.tier.name.toUpperCase()} TIER</span>
                            <div className="ts__price">{money(est.lo)}<span style={{ color: '#9FB4FF' }}>–</span>{money(est.hi)}</div>
                            <div className="ts__weeks">{est.wlo}–{est.whi} weeks · ballpark</div>
                            <ul className="ts__spec">
                                <li>Type <b>{typeLabel}</b></li>
                                <li>Audience <b>{audLabel}</b></li>
                                <li>Inputs <b>{inpLabel}</b></li>
                                <li>Add-ons <b>{addonLabels.length ? addonLabels.length : 'none'}</b></li>
                            </ul>
                            <button className="ts__btn" type="submit" disabled={status === 'sending' || !name || !email}>
                                {status === 'sending' ? 'Sending…' : 'Send my brief →'}
                            </button>
                            <div className="ts__disc">Ballpark only — we confirm the exact scope and price on a short call. No obligation.</div>
                            {status === 'error' && (
                                <div className="ts__msg" style={{ color: '#FFC7D5' }}>
                                    Couldn't send just now. Email your brief to{' '}
                                    <a style={{ color: '#fff', fontWeight: 700 }} href={`mailto:${FALLBACK_EMAIL}?subject=Tool%20Studio%20brief&body=${encodeURIComponent(brief())}`}>{FALLBACK_EMAIL}</a>.
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="ts__empty"><span className="ts__badge" style={{ background: BLUE }}>LIVE ESTIMATE</span>
                            <p style={{ marginTop: 14 }}>Pick a tool type and we'll show a ballpark price and timeline here — updating as you go.</p>
                        </div>
                    )}
                </aside>
            </div>
        </form>
    );
}
